import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Search, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { api, TIPOS, ESTADOS_TTO, BONO_AVISO, OPC, fmtDate, fmtEur, avisoSiguiente } from "@/lib/api";
import { useCentro } from "@/lib/centro";
import { PageTitle, EstadoBadge } from "@/components/Badges";
import { NewTreatmentDialog } from "@/components/NewTreatmentDialog";
import { SyntonicDevices } from "@/components/SyntonicDevices";
import { TomatisDevices } from "@/components/TomatisDevices";
import { useAuth, puedeEco } from "@/lib/auth";
import { PrintButton, PrintHeader } from "@/components/PrintList";
import { TomatisSinContestar } from "@/components/TomatisSinContestar";

const extraCol = { bono: "Modalidad / hueco", syntonic: "Aparato", tomatis: "Notas", wivi: "Notas", sesiones: "Tipo · este mes · recuperar" };
const MODS = [{ id: "SESIÓN QUINCENAL", label: "Quincenales" }, { id: "MENSUALIDAD (SEMANAL)", label: "Mensualidades" }, { id: "SESIÓN SUELTA", label: "Sueltas" }, { id: "", label: "Todas" }];
const SesCtrl = ({ t }) => (
  <span>{(t.modalidad || "").replace("SESIÓN ", "").replace(" (SEMANAL)", "")}{t.control && <> · {t.control.mes_hechas}/{t.control.mes_esperadas} · {t.control.recuperar
    ? <b className="text-amber-700" data-testid={`recuperar-${t.id}`}>{t.control.recuperar} por recuperar</b> : <span className="text-emerald-700">al día</span>}</>}</span>
);
const extraVal = (t) => (t.tipo === "bono" ? [t.modalidad, t.hueco, t.plataforma_fin && `Programa casa hasta ${fmtDate(t.plataforma_fin)}`].filter(Boolean).join(" · ") : t.tipo === "syntonic" ? (t.device_id ? t.aparato : t.aparato_devuelto ? `${t.aparato} (devuelto)` : "En espera de aparato") : t.notas) || "—";

const Progreso = ({ t, tipo }) => {
  if (tipo === "sesiones" && !t.control) return <span className="font-[JetBrains_Mono] text-xs text-slate-500" data-testid={`sueltas-${t.id}`}>{t.realizadas} {t.realizadas === 1 ? "sesión" : "sesiones"}</span>;
  const [h, tot] = t.control && t.modalidad === "MENSUALIDAD (SEMANAL)" ? [t.control.mes_hechas, t.control.mes_esperadas] : [t.realizadas, t.total];
  const aviso = tipo === "bono" && t.total && t.restantes <= BONO_AVISO;
  return (
    <div className="flex items-center gap-2">
      <Progress value={tot ? Math.min((h / tot) * 100, 100) : 0} className="h-1.5" />
      <span className={`font-[JetBrains_Mono] text-xs whitespace-nowrap ${aviso ? "text-amber-700 font-semibold" : "text-slate-500"}`} data-testid={`progreso-${t.id}`}>{h}/{tot}{t.control && t.modalidad === "MENSUALIDAD (SEMANAL)" ? " este mes" : ""}</span>
      {tipo === "bono" && t.estado === "en_proceso" && t.total > 0 && t.restantes <= BONO_AVISO && <span data-testid={`bono-aviso-${t.id}`} className="rounded bg-amber-100 text-amber-800 text-[10px] font-semibold px-1.5 py-0.5 whitespace-nowrap">Quedan {t.restantes}</span>}
    </div>
  );
};

export default function Tratamientos() {
  const { tipo } = useParams();
  const T = TIPOS[tipo];
  const { centro } = useCentro();
  const isAdmin = puedeEco(useAuth().user);
  const [estado, setEstado] = useState("activos");
  const [mod, setMod] = useState("SESIÓN QUINCENAL");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const [tick, setTick] = useState(0);
  const [sel, setSel] = useState([]);
  const nav = useNavigate();
  const convertir = async (modalidad) => {
    if (!modalidad || !window.confirm(`¿Pasar ${sel.length} tratamiento(s) a ${modalidad}? Saldrán de Bonos y aparecerán en Sesiones sin bono.`)) return;
    const { data } = await api.post("/treatments/convertir", { ids: sel, modalidad });
    toast.success(`${data.convertidos} pasados a ${modalidad}`);
    setSel([]);
    setTick((x) => x + 1);
  };
  const marca = (e, tid) => {
    e.stopPropagation();
    setSel((s) => (s.includes(tid) ? s.filter((x) => x !== tid) : [...s, tid]));
  };

  useEffect(() => {
    setRows(null);
    if (estado === "sin_contestar") return;
    api.get("/treatments", { params: { tipo, centro, estado, q, modalidad: tipo === "sesiones" ? mod : "" } }).then((r) => setRows(r.data));
  }, [tipo, centro, estado, q, tick, mod]);

  const terminar = async (e, t) => {
    e.stopPropagation();
    const msg = t.device_id ? `¿Syntonic terminado para ${t.patient.nombre}? El aparato ${t.aparato} quedará libre.` : `¿Syntonic terminado para ${t.patient.nombre}?`;
    if (!window.confirm(msg)) return;
    const { data } = await api.post(`/treatments/${t.id}/finalizar`);
    toast.success(data.aparato_devuelto ? `Syntonic terminado · ${data.aparato_devuelto} libre` : "Syntonic terminado");
    avisoSiguiente(data, toast, nav);
    setTick((x) => x + 1);
  };

  if (!T) return null;
  return (
    <div data-testid={`tratamientos-page-${tipo}`}>
      <div className="no-print"><PageTitle title={T.label} subtitle={tipo === "bono" ? "Sesiones de terapia visual, huecos y pagos" : "Citas, fechas y pagos de cada paciente"}>
        <PrintButton />
        <NewTreatmentDialog tipo={tipo} />
      </PageTitle></div>
      <PrintHeader title={tipo === "sesiones" ? `Sesiones sin bono · ${MODS.find((m) => m.id === mod).label}` : T.label} detalle={[{ id: "activos", label: "Activos" }, ...ESTADOS_TTO, { id: "", label: "Todos" }].find((e) => e.id === estado)?.label} n={rows?.length} />
      {tipo === "syntonic" && <SyntonicDevices onChange={() => setTick((x) => x + 1)} reloadKey={tick} />}
      {tipo === "tomatis" && <TomatisDevices onChange={() => setTick((x) => x + 1)} />}
      {tipo === "sesiones" && (
        <div className="no-print flex flex-wrap gap-2 mb-3" data-testid="modalidad-tabs">
          {MODS.map((m) => (
            <button key={m.id || "all"} data-testid={`mod-tab-${m.label.toLowerCase()}`} onClick={() => setMod(m.id)}
              className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${mod === m.id ? "bg-rose-700 text-white border-rose-700" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}>{m.label}</button>
          ))}
        </div>
      )}
      <div className="no-print flex flex-wrap gap-2 mb-4">
        {[{ id: "activos", label: "Activos" }, ...(tipo === "tomatis" ? [{ id: "sin_contestar", label: "Pendiente de contestar" }] : []), ...ESTADOS_TTO, { id: "", label: "Todos" }].map((e) => (
          <button key={e.id || "all"} data-testid={`estado-tab-${e.id || "todos"}`} onClick={() => setEstado(e.id)}
            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${estado === e.id ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>{e.label}</button>
        ))}
        <div className="relative min-w-[220px]">
          <Search className="h-4 w-4 absolute left-3 top-2.5 text-slate-400" />
          <Input data-testid="treatments-search" placeholder="Buscar paciente o código…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-9 bg-white" />
        </div>
      </div>
      {tipo === "bono" && sel.length > 0 && (
        <div className="no-print mb-3 flex flex-wrap items-center gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm" data-testid="convertir-bar">
          <span className="text-rose-900">{sel.length} seleccionado(s). No son bono, pasar a:</span>
          {OPC.modalidad_ses.map((m) => <button key={m} onClick={() => convertir(m)} data-testid={`convertir-${m.split(" ")[0].toLowerCase()}`} className="rounded-md bg-rose-700 text-white px-3 py-1 text-xs">{m}</button>)}
          <button onClick={() => setSel([])} data-testid="convertir-cancelar" className="ml-auto text-xs underline text-rose-800">Quitar selección</button>
        </div>
      )}
      {estado === "sin_contestar" && tipo === "tomatis" ? <TomatisSinContestar centro={centro} q={q} /> : (
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="treatments-table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>{tipo === "bono" && <th className="no-print w-8" />}{["Paciente", "Centro", "Estado", "Progreso", extraCol[tipo], "Próxima cita", ...(isAdmin ? ["Pagado", "Pendiente"] : []), ...(tipo === "syntonic" ? ["Final"] : [])].map((h) => <th key={h} className="text-left font-semibold px-3 py-2.5 whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows === null && <tr><td colSpan={8} className="p-6 text-center text-slate-400">Cargando…</td></tr>}
            {rows?.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-slate-400" data-testid="treatments-empty">No hay tratamientos en este estado.</td></tr>}
            {rows?.map((t) => (
              <tr key={t.id} data-testid={`treatment-row-${t.id}`} onClick={() => nav(`/tratamiento/${t.id}`)} className="hover:bg-slate-50 cursor-pointer transition-colors">
                {tipo === "bono" && <td className="no-print pl-3" onClick={(e) => marca(e, t.id)}><input type="checkbox" readOnly checked={sel.includes(t.id)} data-testid={`sel-${t.id}`} className="h-4 w-4 accent-rose-700" title="Seleccionar para pasar a sesiones sin bono" /></td>}
                <td className="px-3 py-2.5 font-medium text-slate-900 whitespace-nowrap">{t.patient.nombre}{t.bono_num && <span data-testid={`bono-num-${t.id}`} className="ml-2 rounded bg-indigo-50 text-indigo-800 text-[10px] font-semibold px-1.5 py-0.5">Bono {t.bono_num}</span>}{tipo === "bono" && t.total > 0 && Number(t.sesiones_previas || 0) > t.total && <span data-testid={`no-bono-${t.id}`} title="Tiene más sesiones anteriores que las del bono: probablemente es mensualidad o quincenal" className="ml-2 rounded bg-rose-100 text-rose-800 text-[10px] font-semibold px-1.5 py-0.5">¿No es bono?</span>}</td>
                <td className="px-3 py-2.5 text-xs text-slate-600">{t.patient.centro || "—"}</td>
                <td className="px-3 py-2.5"><EstadoBadge estado={t.estado} /></td>
                <td className="px-3 py-2.5 min-w-[150px]"><Progreso t={t} tipo={tipo} /></td>
                <td className="px-3 py-2.5 text-xs text-slate-600 max-w-[260px] truncate">{tipo === "sesiones" ? <SesCtrl t={t} /> : extraVal(t)}</td>
                <td className="px-3 py-2.5 text-xs text-slate-600 whitespace-nowrap">{fmtDate(t.proxima_cita)}</td>
                {isAdmin && <td className="px-3 py-2.5 font-[JetBrains_Mono] text-xs">{fmtEur(t.pagado)}</td>}
                {isAdmin && <td className={`px-3 py-2.5 font-[JetBrains_Mono] text-xs ${t.pendiente_pago > 0 ? "text-amber-700 font-semibold" : "text-slate-400"}`}>{fmtEur(t.pendiente_pago)}</td>}
                {tipo === "syntonic" && <td className="px-3 py-2">
                  {["pendiente", "en_proceso"].includes(t.estado)
                    ? <button data-testid={`terminar-syntonic-${t.id}`} onClick={(e) => terminar(e, t)} className="inline-flex items-center gap-1 rounded-md border border-emerald-300 px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50 transition-colors whitespace-nowrap"><CheckCircle2 className="h-3.5 w-3.5" />Syntonic finalizado</button>
                    : <span className="text-xs text-slate-400">—</span>}
                </td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}
